import { useEffect, useState } from 'react';
import {
  BlacklistData,
  BlacklistUserInfoData,
  formatBlacklistItem,
  PermissionEnum,
  ResponseError,
} from '@ui/src';
import {
  useApplyApplicationOngoingWarningActionMutation,
  useBatchApplyApplicationOngoingWarningActionMutation,
  useLazyGetApplicationOngoingMonitoringQuery,
  useLazyGetApplicationOngoingStatusQuery,
  useLazyGetApplicationOngoingWarningQuery,
  useLazyGetApplicationWarningGroupWithPaginationQuery,
  useResetApplicationWarningActionMutation,
  useRestartOngoingCheckingMutation,
} from '../../store/services';
import {
  OngoingCheckingStatus,
  OngoingMonitring,
  OngoingStatus,
} from '../../../common/types';
import { Employee } from '../../../../store/dictionaries/types';
import { NewApplicationStatus } from '../../types';

let retryTimeout: NodeJS.Timeout;

const useOngoingMonitoring = ({
  applicationId,
  ongoingMonitoring,
  currentEmployee,
  status,
  activePermissions,
  ongoingCheckingStatus,
}: {
  applicationId?: string;
  ongoingMonitoring?: OngoingMonitring[];
  currentEmployee?: Employee;
  status?: NewApplicationStatus;
  activePermissions?: PermissionEnum[];
  ongoingCheckingStatus?: OngoingCheckingStatus;
}) => {
  const [selectedWarningGroup, setSelectedWarningGroup] = useState('');
  const [blacklistData, setBlacklistData] = useState<BlacklistData | undefined>(
    undefined,
  );
  const [blacklistDataVisible, setBlacklistDataVisible] =
    useState<boolean>(false);
  const [blacklistItem, setBlacklistDataItem] =
    useState<BlacklistUserInfoData>();
  const [blacklistItemVisible, setBlacklistItemVisible] = useState(false);
  const [applyError, setApplyError] = useState('');
  const [selectedWarningId, setSelectedWarningId] = useState('');

  const [
    getApplicationOngoingMonitoring,
    { isLoading: ongoingIsLoading, isFetching: ongoingIsFetching },
  ] = useLazyGetApplicationOngoingMonitoringQuery();
  const [getOngoingMonitoringStatus, { isLoading: ongoingStatusIsLoading }] =
    useLazyGetApplicationOngoingStatusQuery();
  const [
    getApplicationOngoingWarningById,
    { isLoading: isWarningByIdLoading, isFetching: isWarningByIdFetching },
  ] = useLazyGetApplicationOngoingWarningQuery();
  const [
    getApplicationWarning,
    {
      isLoading: paginationInProgress,
      isFetching: paginationIsFetching,
      isSuccess: paginationIsSuccess,
    },
  ] = useLazyGetApplicationWarningGroupWithPaginationQuery();
  const [
    resetApplicationWarningsWarning,
    {
      isLoading: resetTransactionsWarningIsLoading,
      isSuccess: resetTransactionsWarningIsSuccess,
    },
  ] = useResetApplicationWarningActionMutation();
  const [restartOngoingChecking, { isLoading: restartInProgress }] =
    useRestartOngoingCheckingMutation();
  const [
    applyApplicationOngoingWarning,
    { isLoading: applyWarningInProgress },
  ] = useApplyApplicationOngoingWarningActionMutation();
  const [
    batchApplyApplicationOngoingWarning,
    {
      isLoading: batchApplyWarningInProgress,
      isSuccess: batchApplyWarningIsSuccess,
    },
  ] = useBatchApplyApplicationOngoingWarningActionMutation();

  const getApplicationOngoingWarningByIdAction = async ({
    id,
    warningId,
  }: {
    id: string;
    warningId: string;
  }) => {
    const response = await getApplicationOngoingWarningById({
      appId: applicationId as string,
      warningId,
      id,
    });

    if (
      !blacklistData &&
      !blacklistItem &&
      !blacklistDataVisible &&
      !blacklistItemVisible &&
      response
    ) {
      if (response?.data?.data?.blacklistDoc) {
        setBlacklistData(response?.data?.data?.blacklistDoc);
        setBlacklistDataVisible(true);
      } else if (response?.data?.data?.blacklistItem) {
        setBlacklistDataItem(
          formatBlacklistItem(response?.data?.data?.blacklistItem),
        );
        setBlacklistItemVisible(true);
      }
    }
  };

  const getApplicationWarningsAction = async ({
    warningGroupId,
    cursor,
  }: {
    warningGroupId: string;
    cursor: string;
  }) => {
    setSelectedWarningGroup(warningGroupId);

    await getApplicationWarning({
      id: applicationId as string,
      warningGroupId,
      cursor,
    });
  };

  const batchApplyApplicationOngoingWarningAction = async ({
    action,
    comment,
    matchStatus,
  }: {
    action: string;
    comment: string;
    matchStatus?: string;
  }) => {
    setApplyError('');

    const ongoingGroupsWithoutWanings: OngoingMonitring[] = [];

    ongoingMonitoring?.forEach((item) => {
      item?.warnings?.edges?.forEach((warning) => {
        if (!warning.node.action) {
          ongoingGroupsWithoutWanings.push(item);
        }
      });
    });

    if (ongoingGroupsWithoutWanings && ongoingGroupsWithoutWanings.length) {
      await Promise.all(
        ongoingGroupsWithoutWanings?.map(async (item) => {
          const response = (await batchApplyApplicationOngoingWarning({
            appId: applicationId as string,
            body: {
              warningGroupId: item.id,
              action,
              employeeId: currentEmployee?.id as string,
              comment,
              matchStatus,
            },
          })) as { error: ResponseError };

          if (response.error) {
            if (response.error.data.message) {
              if (
                Array.isArray(response.error.data.message) &&
                response.error.data.message[0].property === 'coreError'
              ) {
                setApplyError(response.error.data.message[0].value as string);
              } else if (typeof response.error.data.message === 'string') {
                setApplyError(response.error.data.message);
              } else {
                setApplyError('Internal Server Error. Try Again');
              }
            }
          }

          return response;
        }),
      );
    }
  };

  const onResetTransactionWarningAction = async (
    warningId: string,
    id: string,
  ) => {
    setSelectedWarningId(warningId);

    const response = (await resetApplicationWarningsWarning({
      appId: applicationId as string,
      id,
      warningId,
    })) as { error: ResponseError };

    if (response.error) {
      if (response.error.data.message) {
        if (
          Array.isArray(response.error.data.message) &&
          response.error.data.message[0].property === 'coreError'
        ) {
          setApplyError(response.error.data.message[0].value as string);
        } else if (typeof response.error.data.message === 'string') {
          setApplyError(response.error.data.message);
        } else {
          setApplyError('Internal Server Error. Try Again');
        }
      }
    }
  };

  const applyApplicationOngoingWarningAction = async ({
    id,
    warningId,
    action,
    comment,
    matchStatus,
  }: {
    id: string;
    warningId: string;
    action: string;
    comment: string;
    matchStatus?: string;
  }) => {
    setApplyError('');

    const response = (await applyApplicationOngoingWarning({
      appId: applicationId as string,
      id,
      warningId,
      body: {
        action,
        comment,
        employeeId: currentEmployee?.id as string,
        matchStatus,
      },
    })) as { error: ResponseError };

    if (response.error) {
      if (response.error.data.message) {
        if (
          Array.isArray(response.error.data.message) &&
          response.error.data.message[0].property === 'coreError'
        ) {
          setApplyError(response.error.data.message[0].value as string);
        } else if (typeof response.error.data.message === 'string') {
          setApplyError(response.error.data.message);
        } else {
          setApplyError('Internal Server Error. Try Again');
        }
      }
    }
  };

  const retryOngoinStatusAction = async () => {
    clearTimeout(retryTimeout);

    const response = (await getOngoingMonitoringStatus(
      applicationId as string,
    )) as { data?: { data?: { edges: { node: OngoingCheckingStatus }[] } } };

    if (response?.data?.data) {
      if (
        response?.data?.data?.edges?.find(
          (item) => item.node.status === OngoingStatus.SUCCESS,
        ) ||
        response?.data?.data?.edges?.find(
          (item) => item.node.status === OngoingStatus.FAIL,
        )
      ) {
        clearTimeout(retryTimeout);
      } else {
        retryTimeout = setTimeout(() => {
          retryOngoinStatusAction();
        }, 60000);
      }
    }
  };

  const restartOngoingCheckingAction = async () => {
    const response = (await restartOngoingChecking({
      applicationId: applicationId as string,
    })) as { data?: { data?: OngoingCheckingStatus } };

    if (response?.data) {
      retryOngoinStatusAction();
    }
  };

  useEffect(() => {
    if (
      applicationId &&
      status &&
      status !== NewApplicationStatus.INCOMPLETE &&
      activePermissions?.includes(
        PermissionEnum.APPLICATIONS_CUSTOMERS_AML_LIST,
      )
    ) {
      retryOngoinStatusAction();
    }
  }, [status, applicationId]);

  useEffect(() => {
    if (
      ongoingCheckingStatus &&
      ongoingCheckingStatus?.status === OngoingStatus.SUCCESS &&
      applicationId &&
      status &&
      status !== NewApplicationStatus.INCOMPLETE &&
      activePermissions?.includes(
        PermissionEnum.APPLICATIONS_CUSTOMERS_AML_LIST,
      )
    ) {
      getApplicationOngoingMonitoring(applicationId);
    }
  }, [ongoingCheckingStatus, status, applicationId]);

  useEffect(() => {
    if (paginationIsSuccess) {
      setSelectedWarningGroup('');
    }
  }, [paginationIsSuccess]);

  return {
    ongoingIsLoading,
    ongoingIsFetching,
    ongoingStatusIsLoading,
    applyError,
    applyWarningInProgress,
    applyApplicationOngoingWarningAction,
    batchApplyWarningInProgress,
    batchApplyApplicationOngoingWarningAction,
    restartOngoingCheckingAction,
    restartInProgress,
    batchApplyWarningIsSuccess,
    onResetTransactionWarningAction,
    resetTransactionsWarningIsLoading,
    resetTransactionsWarningIsSuccess,
    selectedWarningId,
    getApplicationWarningsAction,
    paginationInProgress,
    paginationIsFetching,
    selectedWarningGroup,
    blacklistData,
    setBlacklistData,
    blacklistDataVisible,
    setBlacklistDataVisible,
    blacklistItem,
    setBlacklistDataItem,
    blacklistItemVisible,
    setBlacklistItemVisible,
    getApplicationOngoingWarningByIdAction,
    isWarningByIdLoading,
    isWarningByIdFetching,
  };
};

export default useOngoingMonitoring;
